import React from "react";
import { Link } from "react-router-dom";
import "../MailchimpAlternative/MailchimpAlternative.css";
import "../SeoLandingPage/SeoLandingPage.css";
import ManagedFaqSection from "../ManagedFaqSection/ManagedFaqSection";

const relatedAlternatives = [
  ["Mailchimp alternative", "/mailchimp-alternative"],
  ["Brevo alternative", "/brevo-alternative"],
  ["Constant Contact alternative", "/constant-contact-alternative"],
  ["MailerLite alternative", "/mailerlite-alternative"],
  ["Klaviyo alternative", "/klaviyo-alternative"],
];

const sharedContent = {
  relatedLinksAriaLabel: "Related alternative comparisons",
  heroPanelAriaLabel: "alternative comparison",
  heroPanelLabels: ["Templates", "Audience", "Automation", "Reports"],
  comparisonEyebrow: "Comparison overview",
  tableHeaders: ["Area", "Mailsnappy"],
  tableAriaLabel: "Mailsnappy and competitor comparison",
  useCasesEyebrow: "Useful use cases",
  useCasesHeading: "When Mailsnappy may be the right fit",
  faqsEyebrow: "FAQs",
  ctaEyebrow: "Choose the workflow that fits",
  pricingCta: ["View pricing", "/pricing"],
};

const pages = {
  Mailchimp: {
    eyebrow: "Mailchimp alternative",
    h1: "Mailchimp Alternative for Modern Email Marketing",
    comparisonTitle: "Mailsnappy vs Mailchimp",
    faqsHeading: "Mailchimp alternative FAQs",
    intro: (
      <>
        Mailchimp is widely known as a broad marketing platform with email, automation, landing pages, and other campaign tools. Mailsnappy may be a strong fit if your team wants a focused <Link className="linkcolor" to="/email-marketing-software">email marketing software</Link> workflow for designing campaigns, using templates, managing subscribers, and reviewing campaign performance without extra complexity.
      </>
    ),
    cards: [
      ["Template-first campaign building", "Create polished campaigns from responsive templates and move quickly into editing, previewing, and sending."],
      ["Focused email automation", "Build welcome sequences and follow-ups around subscriber journeys rather than managing a broad multi-channel workspace."],
      ["Clear campaign reporting", "Use opens, clicks, engagement, and subscriber activity to improve the next campaign."],
    ],
    overview: "Mailchimp can suit teams looking for a broad marketing suite. Mailsnappy is positioned for teams that want practical email creation, audience organization, segmentation, automation, and analytics in a simpler campaign-focused environment.",
    rows: [
      ["Platform scope", "Focused email marketing platform for templates, campaigns, subscriber tools, automation, and reporting.", "Broad marketing platform with email plus additional marketing and commerce-adjacent tools."],
      ["Campaign creation", "Responsive templates, AI-assisted email design, previews, and campaign-ready layouts.", "Email design and template tools inside a larger marketing workspace."],
      ["Best-fit workflow", "Teams that want to move from idea to email campaign quickly.", "Teams that want email alongside a broader set of marketing channels."],
      ["Automation use case", "Welcome flows, nurturing, and lifecycle follow-ups for email subscribers.", "Marketing automation options across the Mailchimp product ecosystem."],
      ["Audience work", "Subscriber lists, tags, custom fields, signup forms, and segmentation for targeted email sends.", "Audience management features that support broader marketing activities."],
    ],
    useCases: [
      "Launching newsletters and promotional campaigns with ready templates",
      "Creating welcome email sequences for new subscribers",
      "Keeping email campaign work in a focused toolset",
    ],
    ctaHeading: "Design and send email campaigns without extra noise",
    ctaText: "Explore Mailsnappy when your team wants email templates, automation, segmentation, and reporting in one practical workflow.",
    primaryCta: ["Browse email templates", "/templates"],
    secondaryCta: ["Compare pricing", "/pricing"],
    faqs: [
      ["Is Mailsnappy a direct replacement for every Mailchimp feature?", "Not necessarily. Mailchimp covers a broad marketing platform. Mailsnappy is best evaluated as a focused email marketing platform for campaigns, templates, automation, segmentation, and analytics."],
      ["Who should consider Mailsnappy instead of Mailchimp?", "Teams that mainly need professional email campaign creation, subscriber organization, automation, and reporting may prefer Mailsnappy's focused workflow."],
    ],
  },
  Brevo: {
    eyebrow: "Brevo alternative",
    h1: "A Powerful Brevo Alternative for Smarter Email Marketing",
    comparisonTitle: "Which Email Marketing Platform Is Right for You?",
    faqsHeading: "Brevo alternative FAQs",
    intro: (
      <>
        Looking for a simpler and more flexible Brevo alternative? MailSnappy brings email campaigns, automation, audience segmentation, responsive templates, and campaign analytics together in one easy to use platform so you can grow your audience and engage customers without unnecessary complexity
      </>
    ),
    cards: [
      ["Email-First Marketing", "Keep campaign creation, subscriber targeting, automation, and performance tracking centered around email marketing"],
      ["Audience Growth & Segmentation", "Use signup forms, subscriber management, and audience segmentation to grow your list and deliver more relevant campaigns"],
      ["Simple Email Automation", "Create welcome, follow-up, and customer journeys with automated workflows designed around your email marketing needs"],
    ],
    overview: "Both MailSnappy and Brevo provide tools for email marketing, automation, audience management, and campaign analytics. The key difference is their overall focus. MailSnappy is centered around email marketing, while Brevo combines email with broader CRM and multi-channel communication capabilities such as SMS, WhatsApp, and chat",
    rows: [
      ["Primary Focus", "Email marketing campaigns, templates, automation, segmentation, and analytics", "All-in-one marketing and CRM platform covering email, SMS, WhatsApp, chat, and customer relationship tools"],
      ["Channel Strategy", "Built for businesses that want email marketing as a core communication channel", "Designed for businesses managing email alongside SMS, WhatsApp, chat, and CRM workflows"],
      ["Email Campaigns", "Create, customize, and manage email marketing campaigns from one platform", "Create and send email campaigns using a drag-and-drop editor and ready-made templates"],
      ["Audience Management", "Organize subscribers and create focused audience segments for targeted campaigns", "Manage contacts using attributes, lists, filters, and dynamic segments"],
      ["AI Email Design", "AI-assisted email template creation for faster campaign design", "AI tools for email content, subject lines, and send-time optimization"],
      ["Best For", "Businesses looking for a focused email marketing platform with campaigns, automation, templates, segmentation, and analytics", "Businesses looking for a broader multi-channel marketing and CRM platform"],
    ],
    useCases: [
      "Create professional email campaigns with responsive templates",
      "Automate customer journeys and recurring email workflows",
      "Segment subscribers for more relevant and targeted campaigns",
      "Track opens, clicks, conversions, and campaign engagement",
      "Manage your email marketing from one streamlined platform",
    ],
    ctaHeading: "Ready for a Simpler Email Marketing Experience?",
    ctaText: "Create campaigns, automate your customer journeys, segment your audience, and track performance with MailSnappy",
    primaryCta: ["Explore email automation", "/automation"],
    secondaryCta: ["View campaign features", "/features"],
    faqs: [
      ["Is MailSnappy a good alternative to Brevo?", "MailSnappy can be a strong option for businesses that primarily need email marketing campaigns, automation, audience segmentation, responsive templates, and campaign analytics in one focused platform"],
      ["Who should consider MailSnappy?", "MailSnappy is well suited for businesses and marketing teams looking for an email-focused platform to create campaigns, automate workflows, manage subscribers, segment audiences, and measure campaign performance"],
    ],
  },
  "Constant Contact": {
    eyebrow: "Constant Contact alternative",
    h1: "A Powerful Constant Contact Alternative for Smarter Email Marketing",
    comparisonTitle: "Which Email Marketing Platform Is Right for Your Business?",
    faqsHeading: "Constant Contact alternative FAQs",
    intro: (
      <>
        Looking for a flexible Constant Contact alternative? MailSnappy brings email campaigns, responsive templates, audience segmentation, automation, and campaign analytics together in one focused email marketing platform helping growing businesses create better campaigns and engage subscribers more effectively.
      </>
    ),
    cards: [
      ["Email First Marketing", "Keep campaign creation, subscriber targeting, automation, and performance tracking centered around email marketing"],
      ["Audience Growth & Segmentation", "Use signup forms, subscriber management, and audience segmentation to grow your list and deliver more relevant campaigns"],
      ["Simple Email Automation", "Create welcome, follow up, and customer journeys with automated workflows designed around your email marketing needs"],
    ],
    overview: "MailSnappy and Constant Contact both provide tools for creating email campaigns, managing subscribers, automating marketing workflows, and tracking campaign performance. The main difference is how each platform approaches the broader marketing experience. MailSnappy focuses on an email first workflow, while Constant Contact combines email marketing with additional tools for social media, SMS, events, advertising, and other marketing activities",
    rows: [
      ["Primary Focus", "Email marketing campaigns, responsive templates, automation, segmentation, and analytics", "Email marketing combined with broader digital marketing, social, SMS, events, and customer engagement tools"],
      ["Email Campaigns", "Create, customize, and manage email marketing campaigns from one platform", "Create and send email campaigns using a drag and drop editor and professionally designed templates"],
      ["Subscriber Management", "Organize subscribers and use audience segmentation for targeted campaigns", "Manage contacts with lists, tags, contact data, and segmentation tools"],
      ["Email Automation", "Build automated email workflows for welcome, follow up, and customer journeys", "Provides automation paths, behavioral triggers, welcome series, abandoned cart workflows, and other automated campaigns"],
      ["Email Templates", "Responsive email templates for professional campaigns, with AI assisted template creation", "Hundreds of professionally designed, mobile responsive email templates with drag and drop editing"],
      ["AI Email Design", "AI assisted email template creation to help users create campaigns faster", "AI tools can help generate email content and support campaign creation"],
      ["Additional Marketing Channels", "Primarily focused on email marketing and related campaign workflows", "Includes additional tools for SMS, social media, events, advertising, and other marketing activities"],
      ["Best For", "Businesses looking for an email focused platform for campaigns, automation, segmentation, templates, and analytics", "Businesses looking for a broader digital marketing platform with email plus additional marketing channels"],
    ],
    useCases: [
      "Create professional email campaigns with responsive templates",
      "Grow and organize your subscriber list",
      "Segment audiences for more relevant campaigns",
      "Automate repetitive customer journeys",
      "Track campaign performance and subscriber engagement",
      "Keep your email marketing workflow in one focused platform",
    ],
    ctaHeading: "Ready for a Simpler Email Marketing Experience?",
    ctaText: "Create campaigns, grow your audience, automate customer journeys, and track performance with MailSnappy. Build your email marketing workflow around the tools your business actually needs",
    primaryCta: ["See email marketing software", "/email-marketing-software"],
    secondaryCta: ["Browse templates", "/templates"],
    faqs: [
      ["Is MailSnappy a good alternative to Constant Contact?", "MailSnappy can be a strong option for businesses that primarily need email campaigns, responsive templates, audience segmentation, automation, and campaign analytics in one focused platform"],
      ["Who should consider MailSnappy?", "MailSnappy is well suited for businesses and marketing teams that want to create email campaigns, manage subscribers, segment audiences, automate workflows, and monitor campaign performance from one platform"],
    ],
  },
  MailerLite: {
    eyebrow: "MailerLite alternative",
    h1: "A Powerful MailerLite Alternative for Smarter Email Marketing",
    comparisonTitle: "Which Email Marketing Platform Is Right for Your Business?",
    faqsHeading: "MailerLite alternative FAQs",
    intro: (
      <>
        Looking for a flexible MailerLite alternative? MailSnappy helps businesses create professional email campaigns, manage subscribers, segment audiences, automate customer journeys, and track campaign performance from one focused email marketing platform.
      </>
    ),
    cards: [
      ["Email Campaigns Made Simple", "CCreate professional email campaigns with responsive templates designed to keep your message clear, engaging, and consistent across devices"],
      ["Smarter Audience Management", "Organize subscribers, create targeted segments, and deliver more relevant campaigns based on audience interests and engagement"],
      ["Automated Customer Journeys", "Build welcome, follow-up, re-engagement, and other automated workflows that keep subscribers engaged without repetitive manual work"],
    ],
    overview: "MailSnappy and MailerLite both provide tools for email campaigns, subscriber management, segmentation, automation, templates, and performance tracking. MailerLite has expanded beyond newsletters into areas such as landing pages, websites, digital products, e-commerce features, and AI-powered tools. MailSnappy takes a more focused email marketing approach, bringing campaign creation, audience management, automation, templates, and analytics together for businesses that want their email workflow centered around campaigns and customer engagement",
    rows: [
      ["Primary Focus", "Email marketing campaigns, templates, automation, segmentation, and analytics", "Email marketing combined with audience growth, websites, landing pages, automation, and digital selling tools"],
      ["Email Campaigns", "Create, customize, and manage email marketing campaigns from one platform", "Create campaigns using responsive editors, templates, personalization, and campaign optimization tools"],
      ["Email Templates", "Responsive email templates for professional marketing campaigns", "A library of newsletter and campaign templates designed for different marketing needs"],
      ["Audience Management", "Organize subscribers and manage audiences for targeted email campaigns", "Manage subscribers with groups, tags, custom fields, filters, and subscriber profiles"],
      ["Email Automation", "Build automated workflows for welcome, follow-up, re-engagement, and customer journeys", "Multi-step automations with triggers, conditions, actions, e-commerce events, and automation templates"],
      ["AI Email Creation", "AI-assisted email template creation to help speed up campaign design", "AI writing tools and subject-line generation for faster email content creation"],
      ["Best For", "Businesses looking for a focused email marketing workflow covering campaigns, audiences, automation, templates, and analytics", "Businesses looking for email marketing combined with broader website, landing-page, audience-growth, and digital-product capabilities"],
    ],
    useCases: [
      "Create responsive email campaigns without unnecessary complexity",
      "Organize and segment subscribers for targeted communication",
      "Build automated customer journeys and follow-up sequences",
      "Use professional email templates to speed up campaign creation",
      " Monitor campaign engagement and subscriber activity",
      "Keep your core email marketing workflow in one platform",
    ],
    ctaHeading: "Ready to Explore a Smarter MailerLite Alternative?",
    ctaText: "Create engaging email campaigns, organize your audience, automate customer journeys, and track campaign performance with MailSnappy",
    primaryCta: ["Try the AI template designer", "/ai-template-designer"],
    secondaryCta: ["Explore newsletter software", "/newsletter-software"],
    faqs: [
      ["What is the difference between MailSnappy and MailerLite?", "MailerLite combines email marketing with broader tools for landing pages, websites, audience growth, digital products, e-commerce, and AI-powered marketing. MailSnappy focuses its experience around core email marketing activities such as campaigns, subscribers, automation, templates, segmentation, and analytics"],
      ["Is MailSnappy a good MailerLite alternative?", "MailSnappy can be a strong option for businesses that want a focused email marketing platform for campaigns, subscriber management, segmentation, automation, templates, and analytics"],
    ],
  },
  Klaviyo: {
    eyebrow: "Klaviyo alternative",
    h1: "Klaviyo Alternative for Focused Email Campaigns",
    comparisonTitle: "Which Email Marketing Platform Is Right for Your Business?",
    faqsHeading: "Klaviyo alternative FAQs",
    intro: (
      <>
        MailSnappy and Klaviyo both provide tools for email campaigns, audience management, automation, segmentation, and performance tracking. Klaviyo has evolved into a broader B2C CRM and omnichannel marketing platform supporting email, SMS, WhatsApp, mobile push, analytics, customer data, and AI-powered marketing tools. MailSnappy takes a more focused email marketing approach, bringing campaigns, subscriber management, templates, automation, segmentation, and analytics together for businesses that want their marketing workflow centered around email
      </>
    ),
    cards: [
      ["Email-First Campaigns", "Create professional email campaigns with responsive templates and a streamlined workflow built around your email marketing goals"],
      ["Audience Growth & Segmentation", "Grow your subscriber list, organize contacts, and create targeted audience segments for more relevant email campaigns"],
      ["Automated Customer Journeys", "Build welcome, follow-up, re-engagement, and other automated email workflows that keep subscribers engaged without repetitive manual work"],
    ],
    overview: "MailSnappy and Klaviyo both provide tools for email campaigns, audience management, automation, segmentation, and performance tracking. Klaviyo has evolved into a broader B2C CRM and omnichannel marketing platform supporting email, SMS, WhatsApp, mobile push, analytics, customer data, and AI-powered marketing tools. MailSnappy takes a more focused email marketing approach, bringing campaigns, subscriber management, templates, automation, segmentation, and analytics together for businesses that want their marketing workflow centered around email",
    rows: [
      ["Primary Focus", "Email marketing campaigns, templates, automation, segmentation, and analytics", "B2C CRM and omnichannel marketing across email, SMS, WhatsApp, mobile push, analytics, and customer data"],
      ["Email Campaigns", "Create, customize, and manage email marketing campaigns from one platform", "Create and manage email campaigns using templates, personalization, AI tools, and campaign workflows"],
      ["Audience Managemen", "Organize subscribers and manage audiences for targeted email campaigns", "Manage customer profiles, lists, segments, behavioral data, and unified customer information"],
      ["Audience Segmentation", "Segment subscribers to create more relevant campaigns based on audience information and engagement", "Build detailed segments using customer behavior, profile data, events, and real-time activity"],
      ["Email Automation", "Build automated email workflows for welcome, follow-up, re-engagement, and customer journeys", "Build automated flows triggered by events and customer behavior across supported marketing channels"],
      ["Email Templates", "Responsive email templates designed for professional marketing campaigns", "Provides customizable email templates and drag-and-drop campaign creation tools"],
      ["AI Email Tools", "AI-assisted email template creation to help speed up campaign design", "AI tools support email content, subject lines, segments, flows, campaign creation, and broader marketing workflows"],
      ["Marketing Channels", "Primarily focused on email marketing and related audience and automation workflows.", "Supports email, SMS, WhatsApp, mobile push, RCS, social marketing, reviews, and other customer communication channels"],
      ["Analytics & Reporting", "Track email campaign performance, engagement, conversions, and subscriber behavior", "Offers campaign, flow, cross-channel, customer, and marketing analytics with advanced reporting capabilities"],
      ["Best For", "Businesses looking for a focused email marketing platform covering campaigns, subscribers, segmentation, automation, templates, and analytics", "B2C brands that need advanced customer data, ecommerce marketing, omnichannel communication, and complex personalization"],
    ],
    useCases: [
      "Create professional email campaigns with responsive templates",
      "Organize and segment your subscriber audience",
      "Build automated email journeys and follow-up workflows",
      "CCreate campaigns faster with AI-assisted email design",
      "Monitor campaign engagement and subscriber activity",
      "Keep your core email marketing workflow in one focused platform",
    ],
    ctaHeading: "Ready for a Simpler Email Marketing Experience?",
    ctaText: "Create engaging email campaigns, grow your audience, automate customer journeys, and track campaign performance with MailSnappy. Build your email marketing workflow around the tools your business actually needs",
    primaryCta: ["Get Started with MailSnappy", "/"],
    secondaryCta: ["See segmentation tools", "/segmentation"],
    faqs: [
      ["Is MailSnappy a good Klaviyo alternative?", "MailSnappy can be a strong option for businesses that primarily need email campaigns, subscriber management, segmentation, automation, responsive templates, and campaign analytics in one focused platform"],
      ["What is the main difference between MailSnappy and Klaviyo?", "Klaviyo is a broader B2C CRM and omnichannel marketing platform with email, SMS, WhatsApp, mobile push, customer data, analytics, and AI-powered tools. MailSnappy focuses more specifically on email marketing, including campaigns, subscribers, templates, segmentation, automation, and analytics"],
    ],
  },
};

function RelatedPages({ current }) {
  return (
    <div className="alternative-links" aria-label={sharedContent.relatedLinksAriaLabel}>
      {relatedAlternatives
        .filter(([label]) => !label.toLowerCase().startsWith(current.toLowerCase()))
        .map(([label, href]) => <Link key={href} to={href}>{label}</Link>)}
    </div>
  );
}

function competitorPageKey(value) {
  return `${String(value).toLowerCase().replace(/\s+/g, "-")}-alternative`;
}
export default function CompetitorAlternative({ competitor }) {
  const page = pages[competitor] || pages.Brevo;
  const comparisonTitle = page.comparisonTitle || `Mailsnappy vs ${competitor}`;
  const competitorTableAriaLabel = sharedContent.tableAriaLabel.replace("competitor", competitor);
  const faqPageKey = competitorPageKey(competitor);

  return (
    <main className="mailchimp-page">
      <section className="mailchimp-hero">
        <div className="mailchimp-hero-copy">
          <span className="mailchimp-eyebrow">{page.eyebrow}</span>
          <h1>{page.h1}</h1>
          <p>{page.intro}</p>
          <div className="mailchimp-actions">
            <Link to={page.primaryCta[1]}>{page.primaryCta[0]}</Link>
            <Link className="secondary" to={page.secondaryCta[1]}>{page.secondaryCta[0]}</Link>
          </div>
        </div>
        <div className="seo-landing-panel" aria-label={`${competitor} ${sharedContent.heroPanelAriaLabel}`}>
          <div className="seo-landing-window">
            <span />
            <span />
            <span />
          </div>
          <div className="seo-landing-mini-grid">
            {sharedContent.heroPanelLabels.map((label) => <b key={label}>{label}</b>)}
          </div>
        </div>
      </section>

      <section className="mailchimp-proof">
        {page.cards.map(([title, text]) => (
          <article key={title}>
            <h2>{title}</h2>
            <p>{text}</p>
          </article>
        ))}
      </section>

      <section className="mailchimp-comparison">
        <div className="mailchimp-section-heading">
          <span>{sharedContent.comparisonEyebrow}</span>
          <h2>{comparisonTitle}</h2>
          <p>{page.overview}</p>
        </div>

        <div className="comparison-table" role="table" aria-label={competitorTableAriaLabel}>
          <div className="comparison-row comparison-head" role="row">
            <div role="columnheader">{sharedContent.tableHeaders[0]}</div>
            <div role="columnheader">{sharedContent.tableHeaders[1]}</div>
            <div role="columnheader">{competitor}</div>
          </div>
          {page.rows.map(([area, mailsnappy, other]) => (
            <div className="comparison-row" role="row" key={area}>
              <div role="cell">{area}</div>
              <div role="cell">{mailsnappy}</div>
              <div role="cell">{other}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="comparison-support">
        <div>
          <span>{sharedContent.useCasesEyebrow}</span>
          <h2>{sharedContent.useCasesHeading}</h2>
          <ul>
            {page.useCases.map((item) => <li key={item}>{item}</li>)}
          </ul>
        </div>
        <ManagedFaqSection
          variant="inline"
          pageKey={faqPageKey}
          eyebrow={sharedContent.faqsEyebrow}
          heading={page.faqsHeading}
          fallback={page.faqs}
        />
      </section>

      <section className="mailchimp-better">
        <div>
          <span>{sharedContent.ctaEyebrow}</span>
          <h2>{page.ctaHeading}</h2>
          <p>{page.ctaText}</p>
          <RelatedPages current={competitor} />
        </div>
        <Link to={sharedContent.pricingCta[1]}>{sharedContent.pricingCta[0]}</Link>
      </section>
    </main>
  );
}
